import React, { useCallback, useEffect, useState } from "react";
import { useField, useForm } from "react-final-form";
import { CheckCircle, CheckCircleOutline } from "@mui/icons-material";
import { Typography } from "@mui/material";
import { Box } from "@mui/system";

import { Table, TableBody, TableCell, TableHead, TableRow } from "ui";

import analytics from "fond/analytics";
import { loadJson } from "fond/fileValidation";
import { Dropzone } from "fond/widgets";

import { c2sFileMap } from "./files";
import { C2SLayersConfiguration, matchC2SArtifactFile, resolveLayerConfigurationInputLayers } from "./helper";
import { FormData } from "./SetupForm";
import { UploadFileMappingModal } from "./UploadFileMappingModal";

const FILE_ERROR_ERROR_MESSAGE = "Invalid file, please try another file.";

export interface C2SUploadArtifacts {
  costBreakdown: string;
  bom: string;
  layersConfiguration: C2SLayersConfiguration;
}

const UploadConfigurationStep: React.FC<{ isReadOnly?: boolean; versionId: string }> = ({ isReadOnly, versionId }) => {
  const { change } = useForm();
  const {
    input: { value: files },
  } = useField<File[]>("Files.Artifacts");

  const {
    input: { value: scenarioConfigInput },
    meta: { error: scenarioStepError, touched: scenarioTouched },
  } = useField<FormData["ScenarioConfigInput"]>("ScenarioConfigInput", {
    validate: (value) => {
      if (!value?.CostBreakdown.Data || !value?.BillOfMaterials.Data) {
        return "All input files must be provided";
      }
      return undefined;
    },
  });

  const {
    input: { value: coreConfigInput },
    meta: { error: coreStepError, touched: coreTouched },
  } = useField<FormData["CoreConfigInput"]>("CoreConfigInput", {
    validate: (value) => {
      if (!value?.LayersConfiguration.Data) {
        return "All input files must be provided";
      }
      return undefined;
    },
  });

  const stepError = scenarioStepError || coreStepError;
  const touched = scenarioTouched || coreTouched;

  const [fileErrors, setFileErrors] = useState<Partial<Record<keyof C2SUploadArtifacts, string>>>({});
  const [uploadedFileNames, setUploadedFileNames] = useState<Partial<Record<keyof C2SUploadArtifacts, string>>>({});
  const [unmatchedFiles, setUnmatchedFiles] = useState<File[]>([]);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [fileMapping, setFileMapping] = useState<Record<string, keyof C2SUploadArtifacts | "">>({});
  // Track manual mappings that persist after modal is closed
  const [manualMappings, setManualMappings] = useState<Record<string, keyof C2SUploadArtifacts>>({});

  // Process files when uploaded, using fuzzy matching and manual mappings
  useEffect(() => {
    const processFiles = async () => {
      // Create a map of matched files using fuzzy matching and manual mappings
      const matchedFiles = new Map<keyof C2SUploadArtifacts, File>();
      const fileNameMap: Partial<Record<keyof C2SUploadArtifacts, string>> = {};

      for (const file of files) {
        // First check if there's a manual mapping for this file
        const manualMatch = manualMappings[file.name];
        if (manualMatch) {
          matchedFiles.set(manualMatch, file);
          fileNameMap[manualMatch] = file.name;
        } else {
          // Otherwise, try fuzzy matching
          const match = matchC2SArtifactFile(file.name);
          if (match) {
            // Only keep the most recent file for each matched type
            matchedFiles.set(match.id, file);
            fileNameMap[match.id] = file.name;
          }
        }
      }

      // Update the uploaded file names state
      setUploadedFileNames(fileNameMap);

      // Process layers configuration file
      const layersConfigFile = matchedFiles.get("layersConfiguration");
      if (layersConfigFile) {
        try {
          const content = await loadJson(layersConfigFile);
          resolveLayerConfigurationInputLayers(content);
          change("CoreConfigInput.LayersConfiguration.Data", content);
          setFileErrors(({ layersConfiguration, ...prev }) => prev);
        } catch (e) {
          change("CoreConfigInput.LayersConfiguration.Data", null);
          setFileErrors((prev) => ({ ...prev, layersConfiguration: FILE_ERROR_ERROR_MESSAGE }));
          analytics.track("collaboration.cts.files.validation.failed", {
            versionId,
            fileType: "layersConfiguration",
            error: FILE_ERROR_ERROR_MESSAGE,
          });
        }
      }

      // Process cost breakdown file
      const costBreakdownFile = matchedFiles.get("costBreakdown");
      if (costBreakdownFile) {
        try {
          const value = await costBreakdownFile.text();
          change("ScenarioConfigInput.CostBreakdown.Data", value);
          setFileErrors(({ costBreakdown, ...prev }) => prev);
        } catch (e) {
          change("ScenarioConfigInput.CostBreakdown.Data", null);
          setFileErrors((prev) => ({ ...prev, costBreakdown: FILE_ERROR_ERROR_MESSAGE }));
          analytics.track("collaboration.cts.files.validation.failed", {
            versionId,
            fileType: "costBreakdown",
            error: FILE_ERROR_ERROR_MESSAGE,
          });
        }
      }

      // Process bill of materials file
      const bomFile = matchedFiles.get("bom");
      if (bomFile) {
        try {
          const value = await bomFile.text();
          change("ScenarioConfigInput.BillOfMaterials.Data", value);
          setFileErrors(({ bom, ...prev }) => prev);
        } catch (e) {
          change("ScenarioConfigInput.BillOfMaterials.Data", null);
          setFileErrors((prev) => ({ ...prev, bom: FILE_ERROR_ERROR_MESSAGE }));
          analytics.track("collaboration.cts.files.validation.failed", {
            versionId,
            fileType: "bom",
            error: FILE_ERROR_ERROR_MESSAGE,
          });
        }
      }
    };

    processFiles();
  }, [change, files, manualMappings, versionId]);

  const handleFilesChange = useCallback(
    async (newFiles: File[]) => {
      const matchedFiles: Map<keyof C2SUploadArtifacts, File> = new Map();
      const unmatched: File[] = [];

      // Separate files into matched and unmatched, keeping only the latest file per type
      // Check both fuzzy matching and manual mappings
      newFiles.forEach((file) => {
        const fuzzyMatch = matchC2SArtifactFile(file.name);
        const manualMatch = manualMappings[file.name];

        if (manualMatch) {
          // Use manual mapping if it exists (takes precedence)
          matchedFiles.set(manualMatch, file);
        } else if (fuzzyMatch) {
          // Otherwise use fuzzy match
          matchedFiles.set(fuzzyMatch.id, file);
        } else {
          // No match found - add to unmatched
          unmatched.push(file);
        }
      });

      // Create a map of existing files by name for quick lookup
      const existingFileMap = new Map(files.map((file) => [file.name, file]));

      // Remove old files that match the same type as newly uploaded files
      matchedFiles.forEach((newFile, fileType) => {
        // Find and remove any existing file that matches this type
        for (const fileName of existingFileMap.keys()) {
          const existingMatch = matchC2SArtifactFile(fileName) || (manualMappings[fileName] ? { id: manualMappings[fileName], fileName } : null);
          if (existingMatch && existingMatch.id === fileType) {
            existingFileMap.delete(fileName);
          }
        }
        // Add the new file
        existingFileMap.set(newFile.name, newFile);
      });

      // Track file upload (only if not in readonly mode)
      if (!isReadOnly) {
        analytics.track("collaboration.cts.files.uploaded", {
          versionId,
          fileCount: newFiles.length,
          fileTypes: newFiles.map((f) => f.name.split(".").pop() ?? ""),
          automaticMatches: matchedFiles.size,
          unmatchedFiles: unmatched.length,
        });

        // Track auto-matched files
        matchedFiles.forEach((_, matchType) => {
          analytics.track("collaboration.cts.files.auto_matched", {
            versionId,
            matchedType: matchType,
          });
        });
      }

      // Store matched files in the form state
      change("Files.Artifacts", Array.from(existingFileMap.values()));

      // If there are unmatched files, show the mapping modal
      if (unmatched.length > 0) {
        if (!isReadOnly) {
          analytics.track("collaboration.cts.files.manual_mapping.opened", {
            versionId,
            unmatchedCount: unmatched.length,
          });
        }
        setUnmatchedFiles(unmatched);
        // Initialize file mapping with auto-selected values where possible
        const initialMapping: Record<string, keyof C2SUploadArtifacts | ""> = {};
        unmatched.forEach((file) => {
          const lastDotIndex = file.name.lastIndexOf(".");
          const fileExtension = lastDotIndex === -1 ? "" : file.name.slice(lastDotIndex).toLowerCase();
          // Auto-select layersConfiguration for JSON files (only valid option)
          if (fileExtension === ".json") {
            initialMapping[file.name] = "layersConfiguration";
          } else {
            initialMapping[file.name] = "";
          }
        });
        setFileMapping(initialMapping);
        setShowMappingModal(true);
      }
    },
    [files, isReadOnly, change, manualMappings, versionId]
  );

  const renderFileStatus = useCallback(() => {
    return c2sFileMap.ArtifactFiles.map(({ id, file, title }) => {
      let success = false;
      if (id === "costBreakdown") {
        success = !!scenarioConfigInput?.CostBreakdown?.Data;
      } else if (id === "bom") {
        success = !!scenarioConfigInput?.BillOfMaterials?.Data;
      } else if (id === "layersConfiguration") {
        success = !!coreConfigInput?.LayersConfiguration?.Data;
      }

      const hasError = fileErrors[id];
      const uploadedFileName = uploadedFileNames[id];
      const showUploadedName = uploadedFileName && uploadedFileName !== file;

      const IconComponent = success && !hasError ? CheckCircle : CheckCircleOutline;
      const color = hasError ? "error" : success ? "success" : "action";
      return (
        <TableRow key={title}>
          <TableCell>
            <Box display="flex" alignItems="center">
              <IconComponent color={color} sx={{ mr: 1 }} />
              <Typography sx={{ fontWeight: 500 }} data-testid="file-title">
                {title}
              </Typography>
              <Typography variant="body3" sx={{ ml: 1 }} data-testid="file-extensions">
                {showUploadedName ? `(${uploadedFileName})` : `(${file})`}
              </Typography>
              {hasError && (
                <Typography variant="body3" sx={{ ml: 2 }} color="error.main" data-testid="file-status">
                  {fileErrors[id]}
                </Typography>
              )}
            </Box>
          </TableCell>
        </TableRow>
      );
    });
  }, [fileErrors, scenarioConfigInput, coreConfigInput, uploadedFileNames]);

  const handleManualMappingConfirm = useCallback(() => {
    // Create a map of existing files
    const existingFileMap = new Map(files.map((file) => [file.name, file]));

    // Save manual mappings and add files to the existing files
    const newMappings: Record<string, keyof C2SUploadArtifacts> = { ...manualMappings };
    const newUploadedFileNames: Partial<Record<keyof C2SUploadArtifacts, string>> = { ...uploadedFileNames };

    unmatchedFiles.forEach((file) => {
      const mappedType = fileMapping[file.name];
      if (mappedType) {
        // Remove old files that match the same type
        for (const fileName of existingFileMap.keys()) {
          const existingMatch = matchC2SArtifactFile(fileName) || (manualMappings[fileName] ? { id: manualMappings[fileName], fileName } : null);
          if (existingMatch && existingMatch.id === mappedType) {
            existingFileMap.delete(fileName);
          }
        }

        // Add the new file
        existingFileMap.set(file.name, file);
        newMappings[file.name] = mappedType;
        // Update uploaded file names immediately
        newUploadedFileNames[mappedType] = file.name;
      }
    });

    // Track manual mapping confirmation
    const mappingCount = Object.values(fileMapping).filter((type) => type !== "").length;
    analytics.track("collaboration.cts.files.manual_mapping.confirmed", {
      versionId,
      mappingCount,
    });

    // Update manual mappings state
    setManualMappings(newMappings);

    // Update uploaded file names state
    setUploadedFileNames(newUploadedFileNames);

    // Update form state with all files
    change("Files.Artifacts", Array.from(existingFileMap.values()));

    // Close the modal and clear temporary state
    setShowMappingModal(false);
    setUnmatchedFiles([]);
    setFileMapping({});
  }, [files, manualMappings, uploadedFileNames, unmatchedFiles, fileMapping, versionId, change]);

  const handleManualMappingCancel = useCallback(() => {
    // Just close the modal without adding the unmatched files
    setShowMappingModal(false);
    setUnmatchedFiles([]);
    setFileMapping({});
  }, []);

  const handleFileMapChange = useCallback((fileName: string, mappingType: keyof C2SUploadArtifacts | "") => {
    setFileMapping((prev) => ({
      ...prev,
      [fileName]: mappingType,
    }));
  }, []);

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          Artifacts
        </Typography>
        {!isReadOnly && (
          <Dropzone
            acceptedFiles={{
              "application/json": [".json"],
              "text/csv": [".csv"],
            }}
            message="Drag and drop your artifact files or click to browse."
            onDropAccepted={handleFilesChange}
            data-testid="artifacts-dropzone"
          />
        )}
        <Box sx={{ mt: 2 }}>
          <Table size="small" variant="ag-grid-standard">
            <TableHead>
              <TableRow>
                <TableCell>
                  Required input files{" "}
                  {stepError && touched && (
                    <Typography color="error" component="span">
                      * {stepError}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{renderFileStatus()}</TableBody>
          </Table>
        </Box>
      </Box>

      {/* Manual File Mapping Modal */}
      <UploadFileMappingModal
        open={showMappingModal}
        unmatchedFiles={unmatchedFiles}
        fileMapping={fileMapping}
        onFileMapChange={handleFileMapChange}
        onConfirm={handleManualMappingConfirm}
        onCancel={handleManualMappingCancel}
      />
    </Box>
  );
};

export default UploadConfigurationStep;
